import { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase, acceptInvitation } from '../lib/supabase'

const AuthContext = createContext(null)

// localStorage key used to bridge an invitation token across the email-
// confirmation roundtrip. JoinPractice.js writes the token here right
// before calling auth.signUp(). The user then confirms their email and
// lands on /dashboard (Supabase's default redirect), at which point
// useAuth picks the token back up and runs the acceptance.
const PENDING_INVITE_KEY = 'rc_pending_invitation_token'

// Stamp first_signed_in_at on bookkeeper_settings the first time we
// see a logged-in user this session. The .is('first_signed_in_at', null)
// guard makes the UPDATE a no-op for accounts that already have it set.
// Safe to call repeatedly: idempotent at the database level.
//
// For team members (Admin / Assistant) there's no bookkeeper_settings
// row tied to their user id, so the UPDATE matches zero rows. Harmless.
async function stampFirstSignIn(userId) {
  try {
    await supabase
      .from('bookkeeper_settings')
      .update({ first_signed_in_at: new Date().toISOString() })
      .eq('bookkeeper_id', userId)
      .is('first_signed_in_at', null)
  } catch (err) {
    // Don't surface auth UI errors over this. Failure to stamp just
    // delays the welcome email by one day; the next sign-in retries.
    console.error('Could not stamp first_signed_in_at:', err)
  }
}

// Run the pending invitation acceptance, if any.
//
// This runs once per session, just after the user becomes authenticated.
// If localStorage has a pending invitation token (set by JoinPractice
// before signup), we call accept-invitation and clear the key.
//
// Failure cases we handle quietly:
//   - Token missing / malformed: nothing to do
//   - accept-invitation returns "already a member": user already accepted
//     from a previous session, just clean up
//   - accept-invitation returns "email mismatch": the user signed up with
//     a different email than was invited. We leave the token in place so
//     they can attempt sign-in with the right address later. (Edge case.)
//   - Any other error: log and leave the token so the next sign-in retries
async function consumePendingInvitation() {
  let token
  try { token = localStorage.getItem(PENDING_INVITE_KEY) } catch { return }
  if (!token) return

  const { error } = await acceptInvitation(token)

  if (!error) {
    try { localStorage.removeItem(PENDING_INVITE_KEY) } catch {}
    return
  }

  // Treat "already a member" as success: the row exists, we just lost
  // track of having accepted it. Clean up the localStorage either way.
  if (error.message && error.message.toLowerCase().includes('already a member')) {
    try { localStorage.removeItem(PENDING_INVITE_KEY) } catch {}
    return
  }

  console.error('Could not auto-accept invitation:', error.message)
  // Don't clear the token -- next sign-in retries.
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  // Track which user IDs we've already attempted to stamp this session,
  // so back-to-back auth state changes don't trigger duplicate calls.
  // Resets on page reload, which is fine -- the DB guard takes over.
  const stampedRef = useRef(new Set())

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const u = data.session?.user ?? null
      setUser(u)
      setLoading(false)
      if (u && !stampedRef.current.has(u.id)) {
        stampedRef.current.add(u.id)
        stampFirstSignIn(u.id)
        consumePendingInvitation()
      }
    })

    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null

      // Supabase emits onAuthStateChange on every TOKEN_REFRESHED event,
      // which fires whenever the tab regains focus after the token has
      // aged. The user object reference is new each time, even when the
      // underlying identity is unchanged. If we naively setUser(u) here,
      // every component with `user` in a useEffect dependency re-runs --
      // which cascades into refetching the whole app's data and (worst
      // of all) blanks the screen for the duration. Bookkeepers who
      // tab back from accounting software get a white flash + 2-second
      // reload every single time.
      //
      // Bail out if the identity hasn't actually changed. Only propagate
      // real transitions: null -> user, user -> null, or user A -> user B.
      setUser(prev => {
        const prevId = prev?.id ?? null
        const newId  = u?.id    ?? null
        if (prevId === newId) return prev   // same identity: keep old reference
        return u
      })

      if (u && !stampedRef.current.has(u.id)) {
        stampedRef.current.add(u.id)
        stampFirstSignIn(u.id)
        consumePendingInvitation()
      }
    })

    return () => listener.subscription.unsubscribe()
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
